import { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'

import budgetContingencyFactors from '../../consts/budget-contingency-factors'
import budgetRegionalLocations from '../../consts/budget-regional-locations'
import { isToolkitExportActionValue } from '../../consts/toolkit-export-actions'
import {
  ActionSelections,
  FlowAction,
  FlowMetadata,
} from '../../core/types/chat.types'
import useMaxFlowStore from '../../stores/use-max-flow-store'
import { normalizeFlowMetadata } from '../../utils/flow'
import { maxMessageDebug } from '../../utils/max-debug'
import FlowActionBudgetFreeTextForm from './flow-forms/flow-action-budget-free-text-form'
import FlowActionBudgetRadioForm from './flow-forms/flow-action-budget-radio-form'
import FlowActionLangCurrencyForm from './flow-forms/flow-action-lang-currency-form'
import FlowActionMonthYearForm from './flow-forms/flow-action-month-year-form'
import FlowActionYearRangeForm from './flow-forms/flow-action-year-range-form'

type MessageBubbleFollowUpActionsProps = {
  actions: FlowAction[]
  disabled: boolean
  conversationId?: string
  flowMetadata?: FlowMetadata
  variant?: 'default' | 'budget-question'
  onSelectAction: (action: FlowAction, selections?: ActionSelections) => void
}

const useStyles = makeStyles((theme) => ({
  actionsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  actionButton: {
    border: '1px solid var(--ds-border-default, #d7dae4)',
    borderRadius: 8,
    background: 'var(--ds-bg-subtle, #f1f2f7)',
    color: 'var(--ds-fg-primary, #1f2430)',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 160ms ease',
    '&:hover': {
      background: 'var(--ds-bg-subtle-hover, rgba(241, 242, 247, 0.75))',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  actionButtonBudget: {
    border: '1px solid var(--ds-border-default, #d7dae4)',
    borderRadius: 9999,
    background: 'var(--ds-bg-surface, #ffffff)',
    color: 'var(--ds-fg-secondary, #535b6c)',
    padding: '0.625rem 1.25rem',
    fontSize: '0.8125rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 160ms ease',
    '&:hover': {
      background: 'rgba(232, 115, 90, 0.06)',
      borderColor: 'var(--color-coral-sender, #E8735A)',
      color: 'var(--color-coral-sender, #E8735A)',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
}))

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isNaN(parsed) ? undefined : parsed
  }
  return undefined
}

const requiresLangCurrency = (action: FlowAction): boolean =>
  action.actionValue === 'TRANS_LANG_CURR'

const requiresMonthYear = (action: FlowAction): boolean =>
  action.requiredCondition === 'month_and_year' ||
  action.metadata?.requiredCondition === 'month_and_year'

const requiresYearRange = (action: FlowAction): boolean =>
  action.requiredCondition === 'year1_and_year2' ||
  action.metadata?.requiredCondition === 'year1_and_year2'

const requiresBudgetFreeText = (action: FlowAction): boolean =>
  action.actionValue === 'BUDGET_MODIFY_LINE_ITEMS'

const requiresBudgetRegionalRate = (action: FlowAction): boolean =>
  action.actionValue === 'BUDGET_MODIFY_REGIONAL_RATE'

const requiresBudgetContingency = (action: FlowAction): boolean =>
  action.actionValue === 'BUDGET_MODIFY_CONTINGENCY'

const requiresSelection = (action: FlowAction): boolean =>
  requiresLangCurrency(action) ||
  requiresMonthYear(action) ||
  requiresYearRange(action) ||
  requiresBudgetFreeText(action) ||
  requiresBudgetRegionalRate(action) ||
  requiresBudgetContingency(action)

const MessageBubbleFollowUpActions = ({
  actions,
  disabled,
  conversationId,
  flowMetadata,
  variant = 'default',
  onSelectAction,
}: MessageBubbleFollowUpActionsProps) => {
  const classes = useStyles()
  const [expandedAction, setExpandedAction] = useState<string | null>(null)

  const storedExploreSelection = useMaxFlowStore((state) =>
    conversationId ? state.exploreSelectionsByConversation[conversationId] : undefined
  )

  const normalizedFlowMetadata = useMemo(
    () => normalizeFlowMetadata(flowMetadata) || undefined,
    [flowMetadata]
  )

  // Filter out CONVERT_EXCEL and message-level toolkit export actions — those go to the toolkit, not here.
  const visibleActions = useMemo(
    () =>
      actions.filter(
        (action) =>
          action.actionValue !== 'CONVERT_EXCEL' &&
          !isToolkitExportActionValue(action.actionValue)
      ),
    [actions]
  )

  const monthYearDefaults = useMemo(() => {
    if (storedExploreSelection?.condition === 'month_and_year') {
      return {
        month: storedExploreSelection.monthChosen,
        year: storedExploreSelection.yearChosen,
      }
    }
    const params = normalizedFlowMetadata?.parameters as Record<string, unknown> | undefined
    return {
      month: toNumber(normalizedFlowMetadata?.monthChosen) ?? toNumber(params?.month),
      year: toNumber(normalizedFlowMetadata?.yearChosen) ?? toNumber(params?.year),
    }
  }, [normalizedFlowMetadata, storedExploreSelection])

  const yearRangeDefaults = useMemo(() => {
    if (storedExploreSelection?.condition === 'year1_and_year2') {
      return {
        year1: storedExploreSelection.year1,
        year2: storedExploreSelection.year2,
      }
    }
    const params = normalizedFlowMetadata?.parameters as Record<string, unknown> | undefined
    return {
      year1: toNumber(normalizedFlowMetadata?.year1) ?? toNumber(params?.year1),
      year2: toNumber(normalizedFlowMetadata?.year2) ?? toNumber(params?.year2),
    }
  }, [normalizedFlowMetadata, storedExploreSelection])

  useEffect(() => {
    if (disabled) {
      setExpandedAction(null)
    }
  }, [disabled])

  if (visibleActions.length === 0) return null

  const isActionExpanded = (actionValue: string) => expandedAction === actionValue

  const handleActionClick = (action: FlowAction) => {
    if (disabled) {
      maxMessageDebug.event('MessageBubbleFollowUpActions ignored disabled action click', {
        conversationId: conversationId || null,
        actionValue: action.actionValue,
      })
      return
    }

    if (requiresSelection(action)) {
      setExpandedAction((current) => (current === action.actionValue ? null : action.actionValue))
      return
    }

    setExpandedAction(null)
    onSelectAction(action)
  }

  const handleApply = (action: FlowAction, selections: ActionSelections) => {
    onSelectAction(action, selections)
    setExpandedAction(null)
  }

  const buttonClass =
    variant === 'budget-question' ? classes.actionButtonBudget : classes.actionButton

  return (
    <div className={classes.actionsRow}>
      {visibleActions.map((action) => {
        if (!isActionExpanded(action.actionValue)) {
          return (
            <button
              key={action.actionValue}
              type="button"
              className={buttonClass}
              disabled={disabled}
              onClick={() => handleActionClick(action)}
            >
              {action.actionText}
            </button>
          )
        }

        if (requiresLangCurrency(action)) {
          return (
            <FlowActionLangCurrencyForm
              key={action.actionValue}
              title={action.actionText}
              onCancel={() => setExpandedAction(null)}
              onApply={(selections) => handleApply(action, selections)}
            />
          )
        }

        if (requiresMonthYear(action)) {
          return (
            <FlowActionMonthYearForm
              key={action.actionValue}
              title={action.actionText}
              initialMonth={monthYearDefaults.month}
              initialYear={monthYearDefaults.year}
              onCancel={() => setExpandedAction(null)}
              onApply={(selections) => handleApply(action, selections)}
            />
          )
        }

        if (requiresYearRange(action)) {
          return (
            <FlowActionYearRangeForm
              key={action.actionValue}
              title={action.actionText}
              initialYear1={yearRangeDefaults.year1}
              initialYear2={yearRangeDefaults.year2}
              onCancel={() => setExpandedAction(null)}
              onApply={(selections) => handleApply(action, selections)}
            />
          )
        }

        if (requiresBudgetFreeText(action)) {
          return (
            <FlowActionBudgetFreeTextForm
              key={action.actionValue}
              title={action.actionText}
              onCancel={() => setExpandedAction(null)}
              onApply={(selections) => handleApply(action, selections)}
            />
          )
        }

        if (requiresBudgetRegionalRate(action)) {
          return (
            <FlowActionBudgetRadioForm
              key={action.actionValue}
              title={action.actionText}
              options={budgetRegionalLocations}
              otherPlaceholder="Specify custom location"
              onCancel={() => setExpandedAction(null)}
              onApply={(selections) => handleApply(action, selections)}
            />
          )
        }

        if (requiresBudgetContingency(action)) {
          return (
            <FlowActionBudgetRadioForm
              key={action.actionValue}
              title={action.actionText}
              options={budgetContingencyFactors}
              otherPlaceholder="Specify custom contingency factor"
              onCancel={() => setExpandedAction(null)}
              onApply={(selections) => handleApply(action, selections)}
            />
          )
        }

        return null
      })}
    </div>
  )
}

export default MessageBubbleFollowUpActions
